/**
 * EPL Admin - IA Analytics
 * Dashboard completo de métricas de OpenAI
 */

import React, { useState } from 'react';
import { 
  Brain, 
  TrendingUp, 
  DollarSign, 
  Clock,
  Download,
  Loader,
  RefreshCw,
  Calendar
} from 'lucide-react';
import { useEplAIAnalytics } from '../../hooks/useEplAdmin';

const AdminAIAnalytics = () => {
  const [dateRange, setDateRange] = useState({});
  const { analytics, logs, loading, error, pagination, actions } = useEplAIAnalytics(dateRange);

  const StatCard = ({ icon: Icon, label, value, subtitle, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600'
    };

    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
        <div className="text-sm font-medium text-gray-600 mb-1">{label}</div>
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">IA Analytics</h2>
          <p className="text-gray-600">Métricas detalladas de evaluaciones con OpenAI</p>
        </div>
        <button
          onClick={actions.refresh}
          disabled={loading}
          className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Actualizar
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <p className="font-semibold">Error al cargar analytics:</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Summary Stats */}
      {analytics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Brain}
              label="Total Evaluaciones"
              value={analytics.summary.totalEvaluations}
              subtitle="Procesadas con OpenAI"
              color="blue"
            />
            
            <StatCard
              icon={TrendingUp}
              label="Tokens Consumidos"
              value={analytics.summary.totalTokens.toLocaleString()}
              subtitle={`Promedio: ${analytics.summary.avgTokens} por eval`}
              color="purple"
            />
            
            <StatCard
              icon={DollarSign}
              label="Costo Estimado"
              value={`$${analytics.summary.estimatedCost}`}
              subtitle="gpt-4o-mini"
              color="green"
            />
            
            <StatCard
              icon={Clock}
              label="Tiempo de Respuesta"
              value={`${analytics.summary.avgResponseTime}s`}
              subtitle="Promedio por evaluación"
              color="orange"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Daily Usage Chart */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Uso Diario (Últimos 30 días)
              </h3>
              <div className="space-y-2">
                {analytics.dailyUsage.slice(-10).map((day, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20">
                      {new Date(day.date).toLocaleDateString('es', { month: 'short', day: 'numeric' })}
                    </span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                      <div 
                        className="bg-blue-600 h-6 rounded-full flex items-center justify-end px-2"
                        style={{ width: `${(day.evaluations / Math.max(...analytics.dailyUsage.map(d => d.evaluations))) * 100}%` }}
                      >
                        <span className="text-xs text-white font-medium">{day.evaluations}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-16 text-right">
                      {(day.tokens / 1000).toFixed(1)}K
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Feedback Style Distribution */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Distribución de Estilos de Feedback
              </h3>
              <div className="space-y-3">
                {Object.entries(analytics.styleDistribution).map(([style, count]) => {
                  const total = Object.values(analytics.styleDistribution).reduce((a, b) => a + b, 0);
                  const percentage = ((count / total) * 100).toFixed(1);
                  
                  return (
                    <div key={style}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 capitalize">{style}</span>
                        <span className="text-gray-500">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Score Distribution */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Distribución de Scores
              </h3>
              <div className="space-y-3">
                {Object.entries(analytics.scoreRanges).map(([range, count]) => {
                  const total = Object.values(analytics.scoreRanges).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                  
                  return (
                    <div key={range}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{range} puntos</span>
                        <span className="text-gray-500">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Model Info */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Información del Modelo
              </h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Modelo</dt>
                  <dd className="text-sm font-medium text-gray-900">gpt-4o-mini</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Costo Input</dt>
                  <dd className="text-sm font-medium text-gray-900">$0.150 / 1M tokens</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Costo Output</dt>
                  <dd className="text-sm font-medium text-gray-900">$0.600 / 1M tokens</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Tokens totales</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {analytics.summary.totalTokens.toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Costo estimado</dt>
                  <dd className="text-sm font-medium text-green-600">
                    ${analytics.summary.estimatedCost}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </>
      )}

      {/* Evaluation Logs Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Logs de Evaluaciones
          </h3>
          <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
            <Download className="w-4 h-4" />
            Exportar JSONL
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
            Cargando logs...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay logs de evaluaciones
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Lab
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tokens
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Estilo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Modelo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(log.date).toLocaleString('es', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.labName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.userName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          log.score >= 80 ? 'bg-green-100 text-green-800' :
                          log.score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {log.score}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.tokens.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                        {log.feedbackStyle}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {log.model}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={actions.prevPage}
                  disabled={pagination.page === 1 || loading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={actions.nextPage}
                  disabled={pagination.page >= pagination.totalPages || loading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAIAnalytics;
